import { useInView } from '@/hooks/reveal-in-view';
import { FC, useRef } from 'react';

import type {
  ContentfulCardWithImageType,
  ContentfulFileType,
  ParsedContentNode,
} from '@/types';

import { ContentManager } from '../content-manager';
import { Image } from '../image';
import SplitTextAnimation from '../split-text-animation';

interface Props {
  cards: ContentfulCardWithImageType[];
  subtitle: string;
  title: string;
}

interface CardProps {
  content?: ParsedContentNode[];
  delay?: number;
  icon?: ContentfulFileType;
  theme: 'gray' | 'black' | 'blue' | 'light-blue';
  title?: ParsedContentNode[];
}

const themes: CardProps['theme'][] = ['gray', 'black', 'blue', 'light-blue'];

const Card: FC<CardProps> = ({ content, delay, icon, theme, title }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "0px 0px 25% 0px" });

  return (
    <article
      className={`sending-money--card sending-money--card_${theme}`}
      onMouseEnter={({ currentTarget }) => {
        currentTarget.classList.add('hover-animation-bounce');
      }}
      onAnimationEnd={({ currentTarget }) => {
        currentTarget.classList.remove('hover-animation-bounce');
      }}
      style={{
        transform: isInView ? 'none' : `translateY(20px)`,
        opacity: isInView ? 1 : 0,
        transition: `all 0.5s cubic-bezier(0.17, 0.55, 0.55, 1) ${delay}s`,
      }}
      ref={ref}
    >
      <div className="sending-money--card--title-wrapper">
        {icon && (
          <div className="sending-money--card--icon-wrapper">
            <Image
              src={icon as any}
              alt={icon.title}
              className="sending-money--card--icon"
            />
          </div>
        )}
        {title && (
          <ContentManager
            content={title}
            className="sending-money--card--title"
          />
        )}
      </div>
      {content && (
        <ContentManager
          content={content}
          className="sending-money--card--content"
        />
      )}
    </article>
  );
};

export const SendingMoney: FC<Props> = ({ cards, subtitle, title }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "0px 0px 25% 0px" });

  return (
    <section className="sending-money" ref={ref}>
      <div className="sending-money--wrapper">
        <SplitTextAnimation
          htmlElement={<h2 style={{ opacity: isInView ? 1 : 0 }}>{title}</h2>}
          isInView={isInView}
          className="sending-money--title"
        />
        <div className="sending-money--subtitle-wrapper">
          <div className="sending-money--subtitle-circle" />
          <div className="sending-money--subtitle-line" />
          <p
            style={{
              transform: isInView ? 'none' : `translateY(20px)`,
              opacity: isInView ? 1 : 0,
              transition: 'all 0.5s cubic-bezier(0.17, 0.55, 0.55, 1)',
            }}
            className="sending-money--subtitle"
          >
            {subtitle}
          </p>
        </div>
        <div className="sending-money--cards-wrapper">
          {cards.map((card, index) => (
            <Card
              key={index}
              theme={themes[index % 4]}
              icon={card.image}
              title={card.title}
              content={card.description}
              delay={index * 0.2}
            />
          ))}
        </div>
      </div>
    </section>
  );
};
